import React, { useContext, useEffect, useRef, useState } from 'react';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import { locationTopicDescriptor } from 'components/Dashboard/helpers/locationSearchDescriptor';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import { useAuth0 } from '@auth0/auth0-react';
import { store } from 'components/Store';
import normalize from 'components/Dashboard/utilities/normalizeString';
import TextForCell from 'components/Dashboard/Influence/tableComponents/TextForCell';
import TagsCell from 'components/Dashboard/Influence/tableComponents/TagsCell';
import EditableTextCell from 'components/Dashboard/Influence/tableComponents/EditableTextCell';
import CrmContactDummyItem from './CrmContactDummyItem';
import useNewlyAddedContacts from 'components/Dashboard/hooks/useNewlyAddedContacts';
import { Link, useLocation, useParams } from 'react-router-dom';
import ChampionStatus from 'components/Dashboard/Influence/tableComponents/ChampionStatus';
import useSelectCrmContacts from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/useSelectCrmContacts';
import Skeleton from 'react-loading-skeleton';
import getAppSite from 'utils/getAppSite';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { validateEmail } from 'utils/validateEmail';

const CrmContactItem = React.forwardRef(
  (
    {
      item,
      scrollBarRef,
      selectedCrmContacts,
      setSelectedCrmContacts,
      contactIds,
      hitsToRender,
      dummyItem,
      setShowAddTagsModal,
      activeIndex,
    },
    rowRef
  ) => {
    const { setPreviousStoredSelectedCrmContacts } = useSelectCrmContacts();
    const globalState = useContext(store);
    const { state, dispatch } = globalState;
    const { contentResults, setContentResults } = useContentResults();
    const { generalApiCall } = useGeneralApiCall();
    const { activePlan } = state;
    const { enableStakeholders } = activePlan;
    const { CrmContacts } = contentResults;

    const { title, imageId, imageUrl, salutation, email, role, description, id } = item;
    const location = useLocation();
    const params = useParams();
    const { groupId } = params;
    const { searchResultsSection } = useMainSectionParameter(params);
    const { isAuthenticated } = useAuth0();
    const { selectedIds, selectedAll, selectedAllVisible } = selectedCrmContacts;

    const isSelected = selectedIds.includes(item.id) || selectedAll || selectedAllVisible.includes(item.id);

    const { checkContactNewlyAdded, resetNewlyAddedContacts } = useNewlyAddedContacts();
    const [newlyAdded, setNewlyAdded] = useState(checkContactNewlyAdded(id));

    const tagsCellRef = useRef(null);

    const handleCheck = (e) => {
      if (e.target.checked) {
        if (selectedAllVisible.length > 0) {
          const newSelectedIds = [...selectedAllVisible, item.id];
          setPreviousStoredSelectedCrmContacts(selectedCrmContacts);
          setSelectedCrmContacts({
            selectedAll: false,
            selectedIds: [],
            selectedAllVisible: newSelectedIds,
          });
        } else {
          const newSelectedIds = [...selectedIds, item.id];
          setPreviousStoredSelectedCrmContacts(selectedCrmContacts);
          setSelectedCrmContacts({
            selectedAll: false,
            selectedIds: newSelectedIds,
            selectedAllVisible: [],
          });
        }
      } else {
        if (selectedAll) {
          const newSelectedIds = contactIds.filter((id) => id !== item.id);
          setPreviousStoredSelectedCrmContacts(selectedCrmContacts);
          setSelectedCrmContacts({
            selectedIds: newSelectedIds,
            selectedAll: false,
            selectedAllVisible: [],
          });
        } else if (selectedAllVisible.length > 0) {
          const newSelectedIds = selectedAllVisible.filter((id) => id !== item.id);
          setPreviousStoredSelectedCrmContacts(selectedCrmContacts);
          setSelectedCrmContacts({
            selectedIds: [],
            selectedAll: false,
            selectedAllVisible: newSelectedIds,
          });
        } else {
          const newSelectedIds = selectedIds.filter((id) => id !== item.id);
          setPreviousStoredSelectedCrmContacts(selectedCrmContacts);
          setSelectedCrmContacts({
            selectedAll: false,
            selectedIds: newSelectedIds,
            selectedAllVisible: [],
          });
        }
      }
    };

    const locationDescriptor = (path) => {
      return {
        pathname: path,
        search:
          !enableStakeholders || !isAuthenticated
            ? ''
            : searchResultsSection || location.pathname.startsWith('/people/directory')
              ? `?topic-id=all`
              : locationTopicDescriptor(location),
        hash: '',
      };
    };

    const { rememberPosition } = useRememberPosition();

    useEffect(() => {
      let removeNewlyAddedClass;
      if (newlyAdded) {
        removeNewlyAddedClass = setTimeout(() => {
          setNewlyAdded(false);
        }, 3000);
      }
      return () => {
        if (removeNewlyAddedClass) {
          clearInterval(removeNewlyAddedClass);
          resetNewlyAddedContacts();
        }
      };
    }, []);

    const updateContactField = async ({ field, value }) => {
      const fieldToRequestKey = { salutation: 'salutation', email: 'emailAddress' };
      const requestKey = fieldToRequestKey[field];
      if (field === 'email' && value && !validateEmail(value)) {
        createNotification('danger', 'Please enter a valid email address');
        throw new Error('Invalid email');
      }
      const result = await generalApiCall({
        pathname: '/api/crm-contact/update',
        method: 'post',
        requestProperties: { id, [requestKey]: value },
        needsAuthentication: true,
        returnCompleteRequest: true,
      });

      if (result?.status === 200) {
        setContentResults((prev) => {
          const contacts = prev?.CrmContacts;
          const pos = contacts?.hits?.findIndex((c) => c?.id === id);
          if (pos < 0) return prev;
          const newHits = [...contacts.hits];
          const currentHit = newHits[pos];
          const originalFieldKey = field === 'salutation' ? 'originalSalutation' : 'originalEmailAddress';
          let fieldUpdate;
          let originalUpdate;
          if (!value) {
            const restoredDefault =
              field === 'email' ? (currentHit.originalEmailAddress ?? null) : (currentHit.originalSalutation ?? null);
            fieldUpdate = { [field]: restoredDefault };
            originalUpdate = { [originalFieldKey]: null };
          } else if (currentHit[originalFieldKey] == null) {
            fieldUpdate = { [field]: value };
            originalUpdate = { [originalFieldKey]: currentHit[field] ?? null };
          } else {
            fieldUpdate = { [field]: value };
            originalUpdate = {};
          }
          newHits[pos] = { ...currentHit, ...fieldUpdate, ...originalUpdate };
          return { ...prev, CrmContacts: { ...contacts, hits: newHits } };
        });
        const fieldLabel = field === 'email' ? 'Email' : 'Salutation';
        createNotification('success', `${fieldLabel} updated successfully`);
      } else {
        const msg =
          result?.status === 400 && typeof result?.data === 'string' ? result.data : 'Could not save changes';
        createNotification('danger', msg);
        throw new Error(msg);
      }
    };

    if (dummyItem) {
      return <CrmContactDummyItem dummyItem={dummyItem} />;
    }

    const updateChampionStatus = ({ id, value }) => {
      let newContacts = { ...CrmContacts };
      const contactPosition = newContacts?.hits?.findIndex((item) => item?.id === id);
      if (contactPosition >= 0) {
        let newHits = [...newContacts?.hits];
        newHits[contactPosition] = {
          ...newHits[contactPosition],
          championStatus: value,
        };
        newContacts = {
          ...newContacts,
          hits: newHits,
        };
        setContentResults({
          ...contentResults,
          CrmContacts: newContacts,
        });
        if (groupId) {
          dispatch({
            type: 'MODIFY_SECTION',
            parameter: 'contactOnGroupUpdated',
            value: newContacts,
          });
        }
      }
    };

    return (
      <tr
        className={`crm-table-item table-item-alignment-adjustment ${newlyAdded ? 'crm-table-item-newly-added' : ''}`}
        ref={rowRef}
      >
        <td>
          <div className={`crm-table-item-checkbox ${selectedAll ? 'crm-table-item-checkbox-selected-all' : ''}`}>
            <input
              type={'checkbox'}
              checked={isSelected}
              onChange={handleCheck}
              id={`crmContact-${item.id}`}
              disabled={selectedAll}
            />
            <label htmlFor={`crmContact-${item.id}`} />
          </div>
        </td>
        <td className='crm-table-item-name'>
          <div>
            <CrmContactImage imageId={imageId} imageUrl={imageUrl} />
            {title?.endsWith('(Deleted)') ? (
              <TextForCell text={title} />
            ) : (
              <Link
                className='content-item-link'
                to={
                  item.contactId
                    ? locationDescriptor(`/stakeholders/${item.contactId}/${normalize(title)}`)
                    : `/people/directory/update/${id}${location.search}`
                }
                style={{
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  rememberPosition('Stakeholders', scrollBarRef);
                }}
              >
                <TextForCell text={title} type={'title'} />
              </Link>
            )}
          </div>
        </td>
        <td className='crm-table-item-champion-status'>
          <ChampionStatus
            updateChampionStatus={updateChampionStatus}
            contact={item}
            scrollBarPosition={scrollBarRef?.current?.scrollTop}
            conditionsToClose={[scrollBarRef?.current?.scrollLeft, activeIndex]}
          />
        </td>
        <td className={`crm-table-item-role ${getAppSite()}`}>
          <TextForCell text={role} />
        </td>
        <td>
          <TextForCell text={description} />
        </td>
        <td className={`position-relative ${contactHolMemberCondition(email) ? 'pr-5' : ''}`}>
          <EditableTextCell
            value={email}
            onSave={(value) => updateContactField({ field: 'email', value })}
            placeholder='Add email'
          />
          {contactHolMemberCondition(email) && (
            <ContactHolMemberInfoIcon
              className='main-subtle-text'
              style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '15px' }}
            />
          )}
        </td>
        <td
          className='crm-table-item-tags'
          style={{ cursor: item?.tags?.length > 0 ? undefined : 'pointer' }}
          ref={tagsCellRef}
        >
          <TagsCell
            ref={tagsCellRef}
            hitsToRender={hitsToRender}
            item={item}
            setShowAddTagsModal={setShowAddTagsModal}
            setPreviousStoredSelectedCrmContacts={() => setPreviousStoredSelectedCrmContacts(selectedCrmContacts)}
            setSelectedCrmContacts={setSelectedCrmContacts}
            selectedCrmContacts={selectedCrmContacts}
          />
        </td>
        <td className='position-relative'>
          <EditableTextCell
            value={salutation}
            onSave={(value) => updateContactField({ field: 'salutation', value })}
            placeholder='Add salutation'
          />
        </td>
      </tr>
    );
  }
);

export const CrmContactImage = (props) => {
  const { imageId, imageUrl, size } = props;
  const cdnUrl = useRef(`${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`);
  const [imageState, setImageState] = useState({
    loading: true,
    src: cdnUrl.current,
  });
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => {
        setImageState({ loading: false, src: imageUrl });
      };
      img.onerror = () => {
        setImageState({ loading: false, src: cdnUrl.current });
      };
      img.src = imageUrl;
    } else {
      setImageState({ loading: false, src: cdnUrl.current });
    }
  }, [imageUrl]);

  return (
    <>
      {imageState.loading ? (
        <Skeleton circle={true} height={30} width={30} />
      ) : (
        <div
          className='crm-table-img'
          style={{
            backgroundImage: imageUrl
              ? `url('${imageState?.src}')`
              : imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
                ? `url('https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1')`
                : `url('${cdnUrl.current}')`,
            backgroundSize: size ? size : '30px',
            backgroundPosition: 'center center',
            height: size ? size : '30px',
            minWidth: size ? size : '30px',
            borderRadius: '50%',
          }}
        />
      )}
    </>
  );
};

const contactHolMemberCondition = (email) => email === 'contactholmember@parliament.uk';
const ContactHolMemberInfoIcon = ({ style = {}, className = '' }) => {
  return (
    <span
      className={`icon-info ${className}`}
      data-tooltip-content={
        'Approximately 200 peers use the generic email address contactholmember@parliament.uk. It is not possible to send bulk emails to this address'
      }
      data-tooltip-id={`tooltip-mobile`}
      data-tooltip-class-name={`header-tooltip pricing-tooltip-description pricing-tooltip-description`}
      style={{
        ...style,
      }}
    ></span>
  );
};

export { ContactHolMemberInfoIcon, contactHolMemberCondition };

export default CrmContactItem;
